<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useCurrentPageStore } from '@/js/core/stores/current-page-store';
import { useAnnouncementBackgroundOffset } from '@/js/core/composables/useAnnouncementBackgroundOffset';
import UserBar from '@/js/core/components/parts/user-bar/UserBar.vue';
import NavigationPanel from '@/js/core/components/parts/navigation-panel/NavigationPanel.vue';

// Adds instances of stores to be updated by interactions
const store = useCurrentPageStore();
const route = useRoute();

// Info content expand/collapse state
const isInfoExpanded = ref(false);
const contentRef = ref(null);
const injectedContentDiv = ref(null);
const injectedButton = ref(null);
const { updateAnnouncementBackgroundOffset } = useAnnouncementBackgroundOffset(contentRef);

const hasInfoContent = computed(() => {
  return store.infoContent && store.infoContent.trim().length > 0;
});

const routeSlug = computed(() => {
  const routeName = typeof route.name === 'string' ? route.name : '';

  return routeName ? routeName.replace(/[^a-z0-9_-]/gi, '-') : '';
});

const pageContentClass = computed(() => (routeSlug.value ? `${routeSlug.value}-page` : ''));

const mainContentClass = computed(() => (routeSlug.value ? `${routeSlug.value}-main` : ''));

function toggleInfo() {
  isInfoExpanded.value = !isInfoExpanded.value;
  if (injectedButton.value) {
    injectedButton.value.setAttribute('aria-expanded', isInfoExpanded.value.toString());
    injectedButton.value.classList.toggle('is-expanded', isInfoExpanded.value);
  }
  if (injectedContentDiv.value) {
    injectedContentDiv.value.hidden = !isInfoExpanded.value;
    injectedContentDiv.value.classList.toggle('is-expanded', isInfoExpanded.value);
  }
}

// SVG icon markup for injection
const infoIconSvg = `<svg class="page-info-button__icon" preserveAspectRatio="xMidYMid" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="250" cy="250" r="220" vector-effect="non-scaling-stroke"></circle><path class="info-icon-letter" d="M267,110.29v39.86H233V110.29Zm0,77V389.71H233V187.28Z" vector-effect="non-scaling-stroke"></path><path d="M250,34a216.07,216.07,0,0,1,84.07,415A216.07,216.07,0,0,1,165.93,51,214.73,214.73,0,0,1,250,34m0-34C111.93,0,0,111.93,0,250S111.93,500,250,500,500,388.07,500,250,388.07,0,250,0Z" vector-effect="non-scaling-stroke"></path></svg>`;

function injectInfoButton() {
  if (!hasInfoContent.value || !contentRef.value) return;

  const h1 = contentRef.value.querySelector('h1');
  if (!h1 || h1.dataset.infoInjected) return;

  // Mark as injected to prevent duplicates
  h1.dataset.infoInjected = 'true';

  // Make h1 clickable to toggle info
  h1.addEventListener('click', toggleInfo);

  // Create button element
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'page-info-button';
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-controls', 'gutenberg-page-info-content');
  button.innerHTML = infoIconSvg + '<span class="visually-hidden">Show page information</span>';
  button.addEventListener('click', e => {
    e.stopPropagation(); // Prevent double-toggle from h1 click
    toggleInfo();
  });
  h1.appendChild(button);

  // Store reference for toggle updates
  injectedButton.value = button;

  // Create expandable content div
  const contentDiv = document.createElement('div');
  contentDiv.id = 'gutenberg-page-info-content';
  contentDiv.className = 'page-info-content';
  contentDiv.setAttribute('role', 'region');
  contentDiv.innerHTML = store.infoContent;
  contentDiv.hidden = true;
  h1.parentNode.insertBefore(contentDiv, h1.nextSibling);

  // Store reference for toggle updates
  injectedContentDiv.value = contentDiv;
}

function cleanupInjectedElements() {
  if (contentRef.value) {
    const button = contentRef.value.querySelector('.page-info-button');
    if (button) button.remove();
    const contentDiv = contentRef.value.querySelector('.page-info-content');
    if (contentDiv) contentDiv.remove();
  }
  injectedButton.value = null;
  injectedContentDiv.value = null;
  isInfoExpanded.value = false;
}

function updatePageLayout() {
  injectInfoButton();
  updateAnnouncementBackgroundOffset();
}

// Watch for content changes (SPA navigation)
watch(
  () => store.content,
  () => {
    cleanupInjectedElements();
    nextTick(() => {
      updatePageLayout();
    });
  }
);

onMounted(() => {
  // Inject info button after content renders
  nextTick(() => {
    updatePageLayout();
  });
});
</script>

<template>
  <UserBar />

  <NavigationPanel />
  <main id="main-content" :class="mainContentClass">
    <section id="page-content" :class="pageContentClass">
      <div ref="contentRef" class="gutenberg-page-content" v-html="store.content" />
    </section>
  </main>
</template>
